/**
 * GuidedTourContext
 *
 * Provides shared state for guided tours across components.
 * This allows TourHelpButton and GuidedTour to share the same tour state.
 *
 * Tour completion status is stored ONLY in Firestore (server-side).
 * This ensures proper reset when user accounts are deleted/recreated.
 */

import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
// firebase/firestore is lazy-loaded below to keep it out of the critical chunk.
import { getTourSteps, TourStep, TourId } from '../tourSteps';
import { useMediaQuery, useTheme } from '@mui/material';

interface TourState {
  isActive: boolean;
  currentStep: number;
  tourId: TourId | null;
}

interface GuidedTourContextValue {
  /** Current tour state */
  tourState: TourState;
  /** Start a specific tour */
  startTour: (tourId: TourId) => void;
  /** Go to next step */
  nextStep: () => void;
  /** Go to previous step */
  prevStep: () => void;
  /** Skip/end the current tour */
  skipTour: () => void;
  /** Check if a specific tour has been completed */
  hasCompletedTour: (tourId: TourId) => boolean;
  /** Get steps for a tour */
  getSteps: (tourId: TourId) => TourStep[];
  /** Get current step data */
  getCurrentStep: () => TourStep | null;
  /** Loading state */
  isLoading: boolean;
  /** Mark a tour as auto-started (to prevent re-triggering) */
  markAutoStarted: (tourId: TourId) => void;
  /** Check if tour was already auto-started */
  wasAutoStarted: (tourId: TourId) => boolean;
}

const GuidedTourContext = createContext<GuidedTourContextValue | null>(null);

/**
 * Get the Firestore field name for a tour's completion status
 */
const getTourCompletionField = (tourId: TourId): string => {
  switch (tourId) {
    case 'dashboard':
      return 'hasCompletedTour';
    case 'cv-builder':
      return 'hasCompletedCVBuilderTour';
    case 'mes-cv':
      return 'hasCompletedMesCVTour';
    case 'job-first':
      return 'hasCompletedJobFirstTour';
    case 'activation':
      return 'activationCompletedAt';
    default:
      return `hasCompleted${tourId}Tour`;
  }
};

export const GuidedTourProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [tourState, setTourState] = useState<TourState>({
    isActive: false,
    currentStep: 0,
    tourId: null,
  });

  // Track which tours have been auto-started this session
  const autoStartedTours = useRef<Set<TourId>>(new Set());

  // ========================================================================
  // FETCH USER DATA FROM FIRESTORE DIRECTLY
  // Note: AuthContext doesn't expose userData, so we fetch it here
  // ========================================================================
  const [userData, setUserData] = useState<Record<string, any> | null>(null);
  const [userDataLoading, setUserDataLoading] = useState(true);

  useEffect(() => {
    if (!user?.uid) {
      setUserData(null);
      setUserDataLoading(false);
      return;
    }

    setUserDataLoading(true);
    let cancelled = false;
    let unsubscribe: (() => void) | null = null;
    void (async () => {
      const [{ doc, onSnapshot }, { db }] = await Promise.all([
        import('firebase/firestore'),
        import('../../../firebase'),
      ]);
      if (cancelled) return;
      const userDocRef = doc(db, 'users', user.uid);
      unsubscribe = onSnapshot(
        userDocRef,
        (docSnap) => {
          if (docSnap.exists()) {
            setUserData(docSnap.data());
          } else {
            setUserData({});
          }
          setUserDataLoading(false);
        },
        () => {
          setUserData({});
          setUserDataLoading(false);
        }
      );
    })();

    return () => {
      cancelled = true;
      if (unsubscribe) unsubscribe();
    };
  }, [user?.uid]);

  // Combined loading state
  const isLoading = authLoading || userDataLoading;

  const getSteps = useCallback((tourId: TourId): TourStep[] => {
    return getTourSteps(tourId, isMobile);
  }, [isMobile]);

  const getCurrentStep = useCallback((): TourStep | null => {
    if (!tourState.isActive || !tourState.tourId) return null;
    const steps = getSteps(tourState.tourId);
    return steps[tourState.currentStep] || null;
  }, [tourState, getSteps]);

  const hasCompletedTour = useCallback((tourId: TourId): boolean => {
    // Only check Firestore (server-side) for tour completion status
    const completionField = getTourCompletionField(tourId);
    const fieldValue = userData?.[completionField];
    // Support both boolean (true) and truthy values (timestamps) for completion
    const isCompletedInFirestore = !!fieldValue;

    // Return false while loading, then return Firestore value
    const firestoreLoaded = !isLoading && userData !== null && userData !== undefined;
    return firestoreLoaded ? isCompletedInFirestore : false;
  }, [userData, isLoading]);

  const markTourCompleted = useCallback(async (tourId: TourId) => {
    // Mark tour as completed in Firestore only (server-side storage)
    if (!user?.uid) return;

    try {
      const [{ doc, updateDoc, serverTimestamp }, { db }] = await Promise.all([
        import('firebase/firestore'),
        import('../../../firebase'),
      ]);
      const completionField = getTourCompletionField(tourId);
      const userRef = doc(db, 'users', user.uid);
      const completionValue = tourId === 'activation' ? serverTimestamp() : true;
      await updateDoc(userRef, {
        [completionField]: completionValue,
      });
    } catch {
      // Failed to mark tour as completed — best effort.
    }
  }, [user?.uid]);

  const startTour = useCallback((tourId: TourId) => {
    const steps = getSteps(tourId);
    if (steps.length === 0) return;

    // Don't override an already-active tour
    setTourState((prev) => {
      if (prev.isActive && prev.tourId !== tourId) return prev;
      return {
        isActive: true,
        currentStep: 0,
        tourId,
      };
    });
  }, [getSteps]);

  const nextStep = useCallback(() => {
    setTourState((prev) => {
      if (!prev.isActive || !prev.tourId) return prev;

      const steps = getSteps(prev.tourId);
      if (prev.currentStep < steps.length - 1) {
        return { ...prev, currentStep: prev.currentStep + 1 };
      } else {
        // Tour completed
        markTourCompleted(prev.tourId);
        return { isActive: false, currentStep: 0, tourId: null };
      }
    });
  }, [getSteps, markTourCompleted]);

  const prevStep = useCallback(() => {
    setTourState((prev) => {
      if (!prev.isActive || prev.currentStep === 0) return prev;
      return { ...prev, currentStep: prev.currentStep - 1 };
    });
  }, []);

  const skipTour = useCallback(() => {
    if (tourState.tourId) {
      markTourCompleted(tourState.tourId);
    }
    setTourState({ isActive: false, currentStep: 0, tourId: null });
  }, [tourState.tourId, markTourCompleted]);

  const markAutoStarted = useCallback((tourId: TourId) => {
    autoStartedTours.current.add(tourId);
  }, []);

  const wasAutoStarted = useCallback((tourId: TourId): boolean => {
    return autoStartedTours.current.has(tourId);
  }, []);

  // Reset autoStartedTours when user changes (handles logout/login, account recreation)
  // This ensures tours can auto-start again for a new user session
  const previousUidRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (previousUidRef.current !== user?.uid) {
      // User changed (login, logout, or account recreation)
      // Log(`[GuidedTourContext] User changed from ${previousUidRef.current} to ${user?.uid}, resetting autoStartedTours`);
      autoStartedTours.current.clear();
      previousUidRef.current = user?.uid;
    }
  }, [user?.uid]);

  const value: GuidedTourContextValue = {
    tourState,
    startTour,
    nextStep,
    prevStep,
    skipTour,
    hasCompletedTour,
    getSteps,
    getCurrentStep,
    isLoading,
    markAutoStarted,
    wasAutoStarted,
  };

  return (
    <GuidedTourContext.Provider value={value}>
      {children}
    </GuidedTourContext.Provider>
  );
};

export const useGuidedTourContext = (): GuidedTourContextValue => {
  const context = useContext(GuidedTourContext);
  if (!context) {
    // Return a safe no-op fallback instead of crashing
    // This can happen when lazy-loaded components mount before the provider is ready
    return {
      startTour: () => {},
      tourState: { isActive: false, currentStep: 0, tourId: null },
    } as GuidedTourContextValue;
  }
  return context;
};

export default GuidedTourContext;
